import { getOverlayLegendPoints } from 'utils/overlayUtils';
import {
  getOverlayDataKey,
  pointHasTraceData,
  hasOverlayTracesLoading,
  getPointOverlayTraceStatus
} from 'utils/overlayTraceUtils';

export const isTraceOverlayChart = (type, showOverlay, selectedPoints) => {
  if (!showOverlay || !selectedPoints || !selectedPoints.length) {
    return false;
  }
  return type === 'waveform' || type === 'spectrum' || type === 'demod_spectrum';
};

export const getTraceDataKeyForType = type => getOverlayDataKey(null, type);

export const getOverlayLegendPointsForDisplay = ({
  isTraceOverlay,
  selectedPoints,
  feature,
  showOverlay,
  traceDataKey
}) => {
  if (!isTraceOverlay) {
    return selectedPoints || [];
  }
  return getOverlayLegendPoints(selectedPoints, feature, showOverlay, traceDataKey);
};

export const getValidPointsForLegend = (legendPoints, traceDataKey) =>
  legendPoints.filter(point => pointHasTraceData(point, traceDataKey));

export const getChartLineIndexForLegendIndex = (legendIndex, legendPoints, loadedPoints) => {
  if (legendIndex === null || legendIndex === undefined) {
    return null;
  }
  const point = legendPoints[legendIndex];
  if (!point) {
    return null;
  }
  const chartIndex = loadedPoints.indexOf(point);
  return chartIndex >= 0 ? chartIndex : null;
};

export const deriveOverlayChartData = ({
  isTraceOverlay,
  validPoints,
  traceDataKey,
  colors,
  color,
  data
}) => {
  if (isTraceOverlay) {
    if (validPoints.length > 0) {
      return {
        chartData: validPoints.map(point => (
          Array.isArray(point[traceDataKey]) ? point[traceDataKey] : []
        )),
        chartColors: colors && colors.length > 0
          ? colors.slice(0, validPoints.length)
          : validPoints.map(point => point.color || color)
      };
    }
    return { chartData: [], chartColors: [] };
  }

  if (Array.isArray(data[0])) {
    return {
      chartData: data,
      chartColors: colors && colors.length > 0 ? colors : [color]
    };
  }

  return {
    chartData: [data],
    chartColors: [color]
  };
};

export const getOverlayLoadingState = ({
  isTraceOverlay,
  overlayLegendPoints,
  traceDataKey,
  validPoints
}) => {
  const isOverlayTraceLoading = isTraceOverlay &&
    hasOverlayTracesLoading(overlayLegendPoints, traceDataKey);
  const loadedOverlayCount = isTraceOverlay ? validPoints.length : 0;
  const hasPendingOverlayTraces = isTraceOverlay &&
    overlayLegendPoints.length > 0 &&
    loadedOverlayCount === 0 &&
    overlayLegendPoints.some((point) => {
      const status = getPointOverlayTraceStatus(point, traceDataKey);
      return status === 'loading' || status === 'idle';
    });

  return {
    isOverlayTraceLoading,
    loadedOverlayCount,
    hasPendingOverlayTraces,
    showOverlayLoadingOverlay: hasPendingOverlayTraces
  };
};

export const isOverlayAllTracesFailed = ({
  isTraceOverlay,
  isOverlayTraceLoading,
  overlayLegendPoints,
  traceDataKey
}) => isTraceOverlay &&
  !isOverlayTraceLoading &&
  overlayLegendPoints.length > 0 &&
  overlayLegendPoints.every(point =>
    getPointOverlayTraceStatus(point, traceDataKey) === 'error'
  );

export const getTagChartOverlayContext = (props, legendCallbacks, legendState = {}) => {
  const isTraceOverlay = isTraceOverlayChart(
    props.type,
    props.showOverlay,
    props.selectedPoints
  );
  const traceDataKey = getTraceDataKeyForType(props.type);
  const overlayLegendPoints = isTraceOverlay
    ? getOverlayLegendPointsForDisplay({
      isTraceOverlay,
      selectedPoints: props.selectedPoints,
      feature: props.feature,
      showOverlay: props.showOverlay,
      traceDataKey
    })
    : [];
  const validPoints = getValidPointsForLegend(overlayLegendPoints, traceDataKey);
  const overlayLoadingState = getOverlayLoadingState({
    isTraceOverlay,
    overlayLegendPoints,
    traceDataKey,
    validPoints
  });

  const getChartLineIndex = legendIndex => getChartLineIndexForLegendIndex(
    legendIndex,
    overlayLegendPoints,
    validPoints
  );

  const getOverlayLegendProps = (chartData, chartColors, timezone) => buildOverlayLegendProps({
    chartData,
    chartColors,
    timezone,
    isTraceOverlay,
    showOverlay: props.showOverlay,
    selectedPoints: props.selectedPoints,
    legendPoints: overlayLegendPoints,
    traceDataKey,
    type: props.type,
    yUnit: props.yUnit,
    updateSelectedPoints: props.updateSelectedPoints,
    retryOverlayTrace: props.retryOverlayTrace,
    multipleSpeedMarkers: props.multipleSpeedMarkers,
    assetType: props.assetType,
    config: props.config,
    shaftSpeedUnit: props.shaftSpeedUnit,
    // For non-trace overlays (velocity/acceleration/trend multi-series) the
    // legend rows still need per-point speed, which lives on selectedPoints —
    // validPoints is empty in that branch, so fall back to the full selection.
    validPointsForLegend: isTraceOverlay ? validPoints : (props.selectedPoints || []),
    hoveredLegendItem: legendState.hoveredLegendItem,
    activeLegendItem: legendState.activeLegendItem,
    onHoveredLegendItemChange: legendCallbacks.onHoveredLegendItemChange,
    onActiveLegendItemChange: legendCallbacks.onActiveLegendItemChange
  });

  return {
    isTraceOverlay,
    traceDataKey,
    overlayLegendPoints,
    validPoints,
    overlayLoadingState,
    getChartLineIndex,
    getOverlayLegendProps
  };
};

export const buildOverlayLegendProps = ({
  chartData,
  chartColors,
  timezone,
  isTraceOverlay,
  showOverlay,
  selectedPoints,
  legendPoints,
  traceDataKey,
  type,
  yUnit,
  updateSelectedPoints,
  retryOverlayTrace,
  multipleSpeedMarkers,
  assetType,
  config,
  shaftSpeedUnit,
  validPointsForLegend,
  hoveredLegendItem,
  activeLegendItem,
  onHoveredLegendItemChange,
  onActiveLegendItemChange
}) => ({
  chartData: chartData || [],
  chartColors: chartColors || [],
  timezone,
  isTraceOverlay,
  showOverlay,
  selectedPoints,
  legendPoints,
  traceDataKey,
  type,
  yUnit,
  updateSelectedPoints,
  retryOverlayTrace,
  multipleSpeedMarkers,
  assetType,
  config,
  shaftSpeedUnit,
  validPointsForLegend,
  hoveredLegendItem,
  activeLegendItem,
  onHoveredLegendItemChange,
  onActiveLegendItemChange
});
